import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { v4 as uuidV4 } from 'uuid';

import {
  Alert,
  Button,
  Col,
  Form,
  InputGroup,
  Overlay,
  Row
} from 'react-bootstrap';
import { Edit, Undo } from '@material-ui/icons';

import { useApi } from '../hooks/useApi';
import { useNotifications } from '../hooks/useNotifications';
import {
  getLocalizations,
  setEditMessage as setEditMessageAction
} from '../redux/localizationSlice';

import Loading from './Loading';
import styles from './EditableLocalization.module.scss';

export default function EditLocalization({ id, message }) {
  const state = useSelector((state) => state.localizations);
  const dispatch = useDispatch();
  const location = useLocation();
  const { notify } = useNotifications();

  const [show, setShow] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState(null);
  const target = useRef(null);
  const editUuid = useRef(null);

  const { data: localization, ...localizationApi } = useApi(
    `/admin/localizations/${id}`,
    {
      fetchOnMount: false,
      onError: setError
    }
  );

  const setEditMessage = useCallback((id) => {
    if (!id) {
      return dispatch(setEditMessageAction(null));
    }

    editUuid.current = uuidV4();
    dispatch(setEditMessageAction({id, uuid: editUuid.current}));
  }, []);

  useEffect(() => {
    return () => setEditMessage(null);
  }, []);

  useEffect(() => {
    if (show === true) {
      setError(null);
      setEditMessage(id);
      localizationApi.fetch();
    } else {
      localizationApi.setData(null);
    }
  }, [show]);

  useEffect(() => {
    if (state.edit.uuid !== editUuid.current) {
      setShow(false);
    }
  }, [state.edit.uuid]);

  useEffect(() => {
    setEditMessage(null);
  }, [location]);

  useEffect(() => {
    if (show && localization) {
      setValue(localization);
    }
  }, [localization, show]);

  const handleEdit = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setShow(true);
  }, []);

  const handleValueChange = useCallback((locale, e) => {
    setValue((prev) => ({
      ...prev,
      [locale]: {
        ...prev[locale],
        value: e.target.value
      }
    }));
  }, []);

  const handleSetDefault = useCallback(
    (locale) => {
      if (!value) return;
      setValue((prev) => ({
        ...prev,
        [locale]: {
          default: prev[locale].default
        }
      }));
    },
    [value]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault();

      const payload = Object.entries(value).reduce(
        (values, [locale, { value }]) => {
          if (value) {
            values[locale] = value;
          }
          return values;
        },
        {}
      );

      const response = await localizationApi.post(payload);
      if (response) {
        dispatch(getLocalizations(state.locale));
        notify('localization.saved');
        setShow(false);
      }
    },
    [value, state.locale]
  );

  const handleClose = useCallback(() => {
    setEditMessage(null);
    setShow(false);
  }, [setEditMessage]);

  return (
    <>
      <span
        className={
          state.edit.id === id ? 'text-warning border border-warning' : ''
        }>
        {message}
        <Edit
          className={styles.editButton}
          ref={target}
          disabled={false}
          onClick={handleEdit}
        />
      </span>
      <Overlay
        target={target.current}
        show={show}
        placement='bottom'
        onHide={handleClose}
        rootClose>
        {({
          placement: _placement,
          arrowProps: _arrowProps,
          show: _show,
          popper: _popper,
          hasDoneInitialMeasure: _hasDoneInitialMeasure,
          ...props
        }) => (
          <div
            className={styles.overlayContainer}
            onClick={(e) => e?.stopPropagation()}
            {...props}
            style={props.style}>
            {localizationApi.loading && <Loading position='content-center' />}
            <Form className='text-center'>
              <h3 className='d-flex justify-content-between mb-2 mt-0'>{id}</h3>

              {error && (
                <Alert variant='danger'>
                  {state.messages[error.key] ||
                    error.key ||
                    'Tapahtui virhe. / Error occurred.'}
                </Alert>
              )}

              {!error &&
                localization &&
                state.sortedOptions.map((locale) => (
                  <Form.Group as={Row} key={locale} className='mb-1'>
                    <Form.Label column sm={1} className='text-start'>
                      {locale}
                    </Form.Label>

                    <Col sm={11}>
                      <InputGroup>
                        <Form.Control
                          disabled={localizationApi.loading}
                          placeholder={value[locale]?.default || ''}
                          value={value[locale]?.value || ''}
                          onChange={(e) => handleValueChange(locale, e)}
                        />
                        <Button
                          className='ps-2 pe-2'
                          title={
                            state.messages['common.revertToDefault'] +
                            `: ${value[locale]?.default}`
                          }
                          disabled={!value[locale]?.value}
                          onClick={() => handleSetDefault(locale)}>
                          <Undo className={styles.undoIcon} />
                        </Button>
                      </InputGroup>
                    </Col>
                  </Form.Group>
                ))}

              <div className='d-flex justify-content-end mt-2'>
                <Button
                  type='button'
                  className='me-2'
                  variant='outline-secondary'
                  onClick={handleClose}>
                  {state.messages['common.cancel'] || 'Peruuta / Cancel'}
                </Button>

                <Button
                  type='button'
                  onClick={handleSubmit}
                  disabled={localizationApi.loading || !!error}>
                  {state.messages['common.save'] || 'Tallenna / Save'}
                </Button>
              </div>
            </Form>
          </div>
        )}
      </Overlay>
    </>
  );
}
